/**
 * Payment link panel for a GST invoice.
 *
 * - Renders "Create payment link" for invoices with an unpaid balance.
 * - Once created, shows the URL + copy button + QR + live status pill.
 * - Adds a WhatsApp shortcut that pushes the URL to the customer.
 *
 * The panel intentionally re-uses whatever `serverInv` fields
 * `GstInvoiceForm` already loads (cashfree_link_url / cashfree_link_id /
 * cashfree_payment_status) so a hard reload after payment surfaces the
 * updated PAID state without a bespoke poll.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { CreditCard, Copy, ExternalLink, MessageCircle, Loader2, CheckCircle2, RefreshCw } from 'lucide-react';
import { API } from '../../../lib/api';
import { greetName, renderMessage, waUrl } from '../../../lib/waShare';
import { fmtINR, formatErr } from '../../../components/admin/billing/shared';

const STATUS_TONE = {
  ACTIVE:          'bg-sky-100 text-sky-800 border-sky-200',
  PAID:            'bg-emerald-100 text-emerald-800 border-emerald-200',
  PARTIALLY_PAID:  'bg-amber-100 text-amber-800 border-amber-200',
  EXPIRED:         'bg-slate-100 text-slate-600 border-slate-200',
  CANCELLED:       'bg-rose-100 text-rose-800 border-rose-200',
};

const InvoicePaymentLinkCard = ({ invoiceId, serverInv, authHeader, onCreated }) => {
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState('');
  const [justCopied, setJustCopied] = useState(false);

  const linkUrl = serverInv?.cashfree_link_url || '';
  const linkAmount = Number(serverInv?.cashfree_link_amount || 0);
  const status = serverInv?.cashfree_payment_status || (linkUrl ? 'ACTIVE' : '');
  const grandTotal = Number(serverInv?.totals?.grand_total || 0);
  const advance = Number(serverInv?.advance_received || 0);
  const paid = Number(serverInv?.amount_paid || 0);
  const dueNow = Math.max(0, grandTotal - advance - paid);
  const alreadyPaid = serverInv?.status === 'paid' || status === 'PAID';

  const createOrRefresh = async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await axios.post(
        `${API}/gst/invoices/${invoiceId}/payment-link`,
        { note: '' },
        { headers: authHeader() },
      );
      // Bubble up so the parent reloads the invoice + picks up the new
      // cashfree_link_url from the server.
      onCreated?.(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setBusy(false); }
  };

  const forceSync = async () => {
    setSyncing(true); setErr('');
    try {
      // Ask the backend to pull the latest status straight from Cashfree
      // (safety net when a webhook was missed). The parent reloads the
      // invoice so the pill / balance update immediately.
      await axios.post(
        `${API}/gst/invoices/${invoiceId}/refresh-payment`,
        {},
        { headers: authHeader() },
      );
      onCreated?.({}); // trigger parent reload
    } catch (e) {
      setErr(formatErr(e));
    } finally { setSyncing(false); }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      setJustCopied(true);
      setTimeout(() => setJustCopied(false), 1500);
    } catch (e) { /* clipboard blocked — user can long-press instead */ }
  };

  const shareWa = () => {
    const c = serverInv?.customer_snapshot || {};
    const phone = String(c.phone || '').replace(/[^\d]/g, '');
    if (!phone) return;
    const number = serverInv?.number || '';
    const message = renderMessage('payment_link', {
      customer: greetName(c.prefix, c.name), number, amount: fmtINR(linkAmount || dueNow), pay_link: linkUrl || '',
    });
    const wa = waUrl(phone, message);
    window.open(wa, '_blank', 'noopener,noreferrer');
  };

  if (!invoiceId) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 space-y-3" data-testid="cf-payment-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center"><CreditCard size={17} /></div>
          <div>
            <div className="text-sm font-bold text-slate-900">Online payment link</div>
            <div className="text-[11px] text-slate-500">Powered by Cashfree — UPI, cards, netbanking, wallets.</div>
          </div>
        </div>
        {status && (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${STATUS_TONE[status] || STATUS_TONE.ACTIVE}`} data-testid="cf-status">
            {status.replace('_', ' ')}
          </span>
        )}
      </div>

      {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-xs">{err}</div>}

      {alreadyPaid ? (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3 text-sm">
          <CheckCircle2 size={16} className="flex-shrink-0" />
          <span>Invoice paid in full. Nothing more to collect.</span>
        </div>
      ) : !linkUrl ? (
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-sm text-slate-600 flex-1">
            <span className="text-slate-400 mr-1">Balance due:</span>
            <span className="font-mono font-bold text-slate-900">₹ {fmtINR(dueNow)}</span>
          </div>
          <button
            onClick={createOrRefresh}
            disabled={busy || dueNow <= 0}
            className="inline-flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
            data-testid="cf-create-btn"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
            Generate payment link
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px] gap-4 items-start">
          <div className="space-y-2 min-w-0">
            <div className="text-xs text-slate-500">Send this link to the customer:</div>
            <div className="flex items-center gap-1">
              <input
                readOnly
                value={linkUrl}
                className="flex-1 min-w-0 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs font-mono text-slate-800 focus:outline-none"
                data-testid="cf-link-url"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={copy}
                className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-2 py-2 rounded"
                data-testid="cf-copy"
                title="Copy link"
              >{justCopied ? <CheckCircle2 size={12} className="text-emerald-600" /> : <Copy size={12} />}</button>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <a
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 text-xs font-semibold"
                data-testid="cf-open"
              ><ExternalLink size={12} /> Open</a>
              <button
                onClick={shareWa}
                disabled={!serverInv?.customer_snapshot?.phone}
                className="inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white text-[11px] font-semibold px-2 py-1 rounded"
                data-testid="cf-wa"
              ><MessageCircle size={12} /> Send via WhatsApp</button>
              <button
                onClick={createOrRefresh}
                disabled={busy}
                className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 text-slate-600 text-[11px] font-semibold px-2 py-1 rounded"
                data-testid="cf-regen"
                title="Regenerate a fresh link (usually only needed if the amount due changed)"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : 'Regenerate'}
              </button>
              <button
                onClick={forceSync}
                disabled={syncing}
                className="inline-flex items-center gap-1 border border-slate-300 hover:bg-slate-50 text-slate-600 text-[11px] font-semibold px-2 py-1 rounded"
                data-testid="cf-refresh"
                title="Force-check the payment status directly with Cashfree (if the webhook missed)"
              >
                {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                Refresh status
              </button>
            </div>
            <div className="text-[11px] text-slate-500 pt-1">
              Link amount: <span className="font-mono font-semibold text-slate-800">₹ {fmtINR(linkAmount)}</span>
              {serverInv?.cashfree_amount_paid > 0 && (
                <> · Received: <span className="font-mono font-semibold text-emerald-700">₹ {fmtINR(serverInv.cashfree_amount_paid)}</span></>
              )}
            </div>
          </div>
          <div className="flex flex-col items-center gap-2 bg-white border border-slate-200 rounded-lg p-2">
            <QRCodeSVG value={linkUrl} size={120} level="M" />
            <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-wide">Scan to pay</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePaymentLinkCard;
